import React, { useRef, useState } from 'react'
import { Form, InjectedFormProps } from 'redux-form'
import { push } from 'react-router-redux'
import http from 'axios'
import moment from 'moment'

import { Dots, Dot } from 'components/Dots'
import showConfirmModal from 'features/Modal/ConfirmModal/ConfirmModal.action'

import LoginOrRegister from 'features/Auth/LoginOrRegister'
import BillingAccountLinkStep from 'features/Billing/BillingAccountLinkStep'
import { SelectStandardOrPremiumNumberStep } from './SelectStandardOrPremiumNumberStep'
import { UploadDocumentsStep } from './UploadDocumentsStep'
import { SummaryStep } from './SummaryStep'

import analytics from 'features/Analytics/Analytics.utils'

import {
  SearchMobileServiceNumbersDocument,
  useCreateMobileServiceMutation,
} from 'src/generated/graphql'

import {
  IMobileServiceFormProps,
  IMobileServiceFormDispatchProps,
} from './MobileServiceForm.container'

import sMarg from 'styles/modules/margins.scss'

const checkApprovedDocuments = async ban =>
  http({
    method: 'GET',
    url: `api/v1/mobile-services/${ban}/has-approved-documents`,
  }).then(response => response.data.hasApprovedDocuments || false)

export const MobileServiceFormComponent = (
  props: InjectedFormProps & IMobileServiceFormProps & IMobileServiceFormDispatchProps
) => {
  const hasApprovedDocuments = useRef(false)
  const [step, setStep] = useState('SELECT')

  const [createMobileService, createMobileServiceStatus] = useCreateMobileServiceMutation()

  const { formData, change, onCancel, handleSubmit, dispatch, setHeadings, isLoggedIn } = props

  const showConfirmation = response => {
    const plannedDeliveryDate = moment(response.case?.plannedDeliveryDate).format('DD MM YYYY')
    const modalOptions = !!response.case
      ? {
          title: 'We are processing your request',
          message: `
              You will be able to use your mobile service after approval of provided documents.
              Planned delivery date: ${plannedDeliveryDate}
          `,
        }
      : {
          title: 'Great news, you mobile number is ready.',
          message: 'You can start using it right away.',
        }

    return dispatch(
      showConfirmModal({
        ...modalOptions,
        textSize: 'sm',
        hideClose: true,
        noClickClose: true,
      })
    )
  }

  const prevStep = () => {
    switch (step) {
      case 'SUMMARY':
        setStep(hasApprovedDocuments.current ? 'BAN' : 'UPLOAD')
        break

      case 'UPLOAD':
        setStep('BAN')
        break

      case 'BAN':
        setStep(isLoggedIn ? 'SELECT' : 'LOGIN')
        break

      case 'LOGIN':
        setStep('SELECT')
        break
    }
  }

  const trackAnalytics = payload => {
    payload.data?.mobileServiceCreate &&
      analytics.track({
        action: 'track',
        event: 'Create - VMS',
        properties: {
          category: 'Create Number',
          label: 'VMS',
          value: payload.data?.mobileServiceCreate.service?.monthlyCharge || 0,
        },
      })

    return payload
  }

  const nextStep = async () => {
    if (step === 'BAN' && formData.ban) {
      formData.isLoading = true
      await checkApprovedDocuments(formData.ban)
        .then(result => {
          hasApprovedDocuments.current = result
        })
        .finally(() => {
          formData.isLoading = false
        })
    }

    if (step === 'SUMMARY') {
      await createMobileService({
        variables: {
          ban: formData.ban,
          number: formData.number,
          documents: formData.uploadedFiles?.map(file => file.hash),
          isPremiumNumber: formData.isPremiumNumber,
          endUserType: formData.endUserType,
        },
        refetchQueries: [{ query: SearchMobileServiceNumbersDocument, variables: { query: '' } }],
      }).then(payload => {
        payload.data?.mobileServiceCreate &&
          trackAnalytics(payload) &&
          showConfirmation(payload.data?.mobileServiceCreate).then(() => {
            dispatch(push(`/dashboard/numbers/${payload.data?.mobileServiceCreate.service.id}`))
          })
      })

      return
    }

    switch (step) {
      case 'SELECT':
        setStep(isLoggedIn ? 'BAN' : 'LOGIN')
        break

      case 'LOGIN':
        setStep('BAN')
        break

      case 'BAN':
        setStep(hasApprovedDocuments.current ? 'SUMMARY' : 'UPLOAD')
        break

      case 'UPLOAD':
        setStep('SUMMARY')
        break
    }
  }

  const commonProps = {
    formData,
    change,
    onBack: prevStep,
    loading: createMobileServiceStatus.loading || formData.isLoading,
    setHeadings,
  }

  const renderStep = () => {
    switch (step) {
      case 'SELECT':
        return (
          <SelectStandardOrPremiumNumberStep
            {...commonProps}
            onBack={onCancel}
            clearNumber={() => change('number', '')}
          />
        )

      case 'BAN':
        return <BillingAccountLinkStep {...commonProps} prev={prevStep} />

      case 'LOGIN':
        return <LoginOrRegister {...commonProps} afterSubmit={nextStep} />

      case 'SUMMARY':
        return <SummaryStep {...commonProps} />

      case 'UPLOAD':
        return <UploadDocumentsStep {...commonProps} />

      default:
        return <div>Unknown step {step}</div>
    }
  }

  const getSteps = () => {
    const steps = [] as string[]

    steps.push('SELECT')
    if (!isLoggedIn) {
      steps.push('LOGIN')
    }
    steps.push('BAN')
    if (!hasApprovedDocuments.current) {
      steps.push('UPLOAD')
    }
    steps.push('SUMMARY')

    return steps
  }

  return (
    <>
      <Dots className={sMarg.mb60to30}>
        {getSteps().map((s, index) => (
          <Dot key={index} active={s === step} />
        ))}
      </Dots>

      {step !== 'LOGIN' ? (
        <Form onSubmit={handleSubmit(nextStep)}>{renderStep()}</Form>
      ) : (
        renderStep()
      )}
    </>
  )
}



// WEBPACK FOOTER //
// src/features/MobileServices/MobileServiceForm/MobileServiceForm.tsx